/**
 * Polymarket API Service
 * Fetches trending prediction markets to display in the ticker.
 */

export interface PolymarketEvent {
  id: string;
  title: string;
  active: boolean;
  markets?: {
    question: string;
    outcomePrices?: string[];
  }[];
}

export async function fetchPolymarketUpdates(lang: string = 'en'): Promise<string[]> {
  try {
    // Fetch more events so we can filter them
    const res = await fetch('https://gamma-api.polymarket.com/events?limit=50&active=true');
    
    if (!res.ok) {
      throw new Error('Polymarket API error');
    }

    const data: PolymarketEvent[] = await res.json();
    const updates: string[] = [];

    // Filter for Israel/Middle East related events
    const keywords = ['israel', 'lebanon', 'iran', 'gaza', 'middle east', 'hezbollah', 'hamas'];
    const filteredEvents = data.filter(event => 
      keywords.some(kw => event.title.toLowerCase().includes(kw))
    );

    if (filteredEvents.length === 0) {
      const isHe = lang.startsWith('he');
      const prefix = isHe ? 'פולימרקט:' : 'Polymarket:';
      const msg = isHe 
        ? 'שקט בצורה מחשידה בכל הגזרות (ישראל, לבנון, איראן, ועזה). תישאר ערני.'
        : 'It\'s surprisingly quiet right now regarding Israel, Lebanon, Iran, and Gaza.';
      return [`📈 ${prefix} ${msg}`];
    }

    for (const event of filteredEvents) {
      if (event.markets && event.markets.length > 0) {
        const topMarket = event.markets[0];
        // If it's a binary market, price is roughly probability
        if (topMarket && topMarket.outcomePrices && topMarket.outcomePrices.length > 0) {
          const priceString = topMarket.outcomePrices[0];
          if (priceString) {
            const prob = Math.round(parseFloat(priceString) * 100);
            if (!isNaN(prob) && prob > 0 && prob < 100) {
              const prefix = lang.startsWith('he') ? 'תחזית שוק:' : 'Polymarket:';
              updates.push(`📈 ${prefix} ${prob}% chance of ${event.title}`);
            }
          }
        }
      }
    }
    
    // Return top 2 to not bloat the ticker
    return updates.slice(0, 2);
  } catch (error) {
    console.error('Failed to fetch Polymarket updates:', error);
    return [];
  }
}
